import { getLensUriRouter, } from "./lensUriRouter.js";
export const HOST_URI_ROOT = "app://lens-host/v1/";
function asRouter(engineOrRouter) {
    return typeof engineOrRouter?.register === "function"
        ? engineOrRouter
        : getLensUriRouter(engineOrRouter);
}
function endpoint(uri) {
    const value = String(uri || "");
    if (!value.startsWith(HOST_URI_ROOT))
        return null;
    const [kind, ...nameParts] = value.slice(HOST_URI_ROOT.length).split("?")[0].split("/");
    let name;
    try {
        name = decodeURIComponent(nameParts.join("/"));
    }
    catch {
        name = nameParts.join("/");
    }
    return { kind, name };
}
/**
 * Install the renderer's protocol that any lens can use without a custom JS
 * bridge:
 *
 *   app://lens-host/v1/subscribe/<channel>  retained host -> lens channel
 *   app://lens-host/v1/event/<name>         lens -> host event, auto-acked
 *   app://lens-host/v1/request/<name>       lens -> host request, host answers
 */
export async function installLensHostUriProtocol(engineOrRouter, onTrace = () => undefined) {
    const router = asRouter(engineOrRouter);
    const listeners = new Set();
    const emit = (entry) => {
        onTrace(entry);
        for (const listener of listeners)
            listener(entry);
    };
    const registration = await router.register({
        scheme: "app",
        prefix: "lens-host/v1/",
        async handleRequest(request) {
            const parsed = endpoint(request.uri);
            if (!parsed?.name) {
                await request.respondJson({ ok: false, error: "missing endpoint name" }, {
                    code: 400,
                    description: "Bad Request",
                });
                return;
            }
            let body = null;
            if (request.text) {
                try {
                    body = request.json();
                }
                catch {
                    body = request.text;
                }
            }
            if (parsed.kind === "subscribe") {
                request.retain(parsed.name, { body });
                emit({ phase: "subscribed", channel: parsed.name, uri: request.uri, body });
                return;
            }
            if (parsed.kind === "event") {
                emit({ phase: "event", name: parsed.name, uri: request.uri, body });
                await request.respondJson({ ok: true, event: parsed.name });
                return;
            }
            if (parsed.kind === "request") {
                emit({
                    phase: "request",
                    name: parsed.name,
                    identifier: request.identifier,
                    uri: request.uri,
                    body,
                });
                return;
            }
            await request.respondJson({ ok: false, error: `unknown endpoint kind '${parsed.kind}'` }, {
                code: 404,
                description: "Not Found",
            });
        },
        cancelRequest(request) {
            emit({ phase: "cancelled", identifier: request.identifier, uri: request.uri });
        },
    });
    emit({ phase: "listening", uri: HOST_URI_ROOT, installedAt: Date.now() });
    return {
        router,
        registration,
        send: (channel, data, options) => router.send(channel, data, options),
        sendJson: (channel, data, options) => router.sendJson(channel, data, options),
        respond: (identifier, data, options) => router.respond(identifier, data, options),
        respondJson: (identifier, data, options) => router.respondJson(identifier, data, options),
        onEvent(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        dispose() {
            listeners.clear();
            registration.dispose();
        },
    };
}
//# sourceMappingURL=lensHostUriProtocol.js.map